import getCorrespondingAnalysisSteps from 'notebook/notes/groups/analysis-template/getCorrespondingAnalysisSteps';
import {JSONValue} from 'util/Types';

interface StepReference {
  id: string;
  stepType: string;
}

interface AnalysisStep {
  stepType: string;
  config: JSONValue;
}

interface TemplateConfigVariable {
  id: string;
  configVariableKey: string;
  pipelineStepDomain: StepReference;
}

function valueAtPath(config: JSONValue, path: string): JSONValue | undefined {
  let value: JSONValue | undefined = config;
  for (const segment of path.split('.')) {
    if (Array.isArray(value)) {
      value = value[Number(segment)];
    } else if (value != null && typeof value === 'object') {
      value = (value as {[key: string]: JSONValue})[segment];
    } else {
      return undefined;
    }
  }
  return value;
}

/**
 * The value an analysis created from a template holds for each template input, keyed by the input's id.
 * Each input's value is read from the config of the analysis step that corresponds to the input's template
 * step (see `getCorrespondingAnalysisSteps`), at the input's `configVariableKey` path. Inputs whose step or
 * value cannot be found are left out.
 */
export default function getEmbeddedAnalysisInputValues(
  configVariables: ReadonlyArray<TemplateConfigVariable>,
  templateSteps: ReadonlyArray<StepReference>,
  analysisSteps: ReadonlyArray<AnalysisStep>
): Record<string, JSONValue> {
  const correspondingSteps = getCorrespondingAnalysisSteps(templateSteps, analysisSteps);
  const values: Record<string, JSONValue> = {};
  for (const variable of configVariables) {
    const analysisStep = correspondingSteps.get(variable.pipelineStepDomain.id);
    if (analysisStep == null) {
      continue;
    }
    const value = valueAtPath(analysisStep.config, variable.configVariableKey);
    if (value !== undefined) {
      values[variable.id] = value;
    }
  }
  return values;
}
